import { makeAutoObservable, observable, runInAction } from "mobx"
import { ModalTypes } from "../utils/customer-modal-helper"
import { CreateCustomer, Customer, EditCustomer } from "../models/customer-models"
import { CreateEditSite, Site } from "features/organizations/sites/models/sites-models"
import { Asset, CreateAsset, EditAsset } from "features/organizations/assets/models/asset-models"
import {
    AssetSection,
    CreateAssetSection,
    EditingAssetSectionWindow,
    LinerLocation,
    LinerRegion,
} from "features/organizations/asset-sections/models/asset-section-models"
import { deleteCustomer, editCustomer, getAllCustomers, getSitesForCustomer, postNewCustomer } from "../services/customer-services"
import { deleteSite, editSite, postNewSite } from "features/organizations/sites/services/site-services"
import { deleteAsset, editAsset, postNewAsset } from "features/organizations/assets/services/asset-services"
import { deleteAssetSection, editAssetSection, postNewAssetSection } from "features/organizations/asset-sections/services/asset-section-services"
import { isSuccess, returnOnFail, withLoading } from "stores/stores-helper"

export interface CustomerStoreResults {
    passed: boolean
    message: string
    entityId?: string
}

class CustomerStore {
    customerMap: Record<string, Customer> = observable.object({})
    siteMap: Record<string, Site> = observable.object({})
    assetMap: Record<string, Asset> = observable.object({})
    assetSectionMap: Record<string, AssetSection> = observable.object({})
    linerLocationMap: Record<string, LinerLocation> = observable.object({})
    linerRegionMap: Record<string, LinerRegion> = observable.object({})

    customersLoading: boolean = false
    customerLoading: boolean = false
    sitesLoadingForCustomer: Record<string, boolean> = observable.object({})
    customersStore: any

    constructor() {
        makeAutoObservable(this)
    }

    get customers() {
        return Object.values(this.customerMap)
    }
    get sites() {
        return Object.values(this.siteMap)
    }
    get assets() {
        return Object.values(this.assetMap)
    }
    get assetSections() {
        return Object.values(this.assetSectionMap)
    }

    get linerLocations() {
        return Object.values(this.linerLocationMap)
    }

    get linerRegions() {
        return Object.values(this.linerRegionMap)
    }

    getCustomerNameById(customerId: string | undefined) {
        return customerId ? this.customerMap[customerId]?.name?.toLowerCase() : undefined
    }

    getSiteNameById(siteId: string | undefined) {
        return siteId ? this.siteMap[siteId]?.name?.toLowerCase() : undefined
    }

    loadAllCustomers = async () => {
        return await withLoading(
            val => (this.customersLoading = val),
            async () => {
                const data = await getAllCustomers()
                runInAction(() => {
                    for (const customer of data) {
                        this.customerMap[customer.id] = observable.object(customer)
                        for (const site of customer.sites ?? []) {
                            this.siteMap[site.id] = observable.object(site)
                            for (const asset of site.assets ?? []) {
                                this.assetMap[asset.id] = asset
                                for (const section of asset.assetSections ?? []) {
                                    this.assetSectionMap[section.id] = section
                                    for (const location of section.linerLocations ?? []) {
                                        this.linerLocationMap[location.id] = location
                                        for (const region of location.linerRegions ?? []) {
                                            this.linerRegionMap[region.id] = region
                                        }
                                    }
                                }
                            }
                        }
                    }
                })
                return this.customers
            },
        )
    }

    loadSitesForCustomer = async (customerId: string) => {
        if (this.sitesLoadingForCustomer[customerId]) {
            return
        }

        runInAction(() => {
            this.sitesLoadingForCustomer[customerId] = true
        })

        const data = (await getSitesForCustomer(customerId)) || []
        runInAction(() => {
            this.customerMap[customerId].sites = data

            for (let site of data) {
                this.siteMap[site.id] = site

                for (let asset of site.assets || []) {
                    this.assetMap[asset.id] = asset

                    for (let assetSection of asset.assetSections || []) {
                        this.assetSectionMap[assetSection.id] = assetSection
                    }
                }
            }

            this.sitesLoadingForCustomer[customerId] = false
        })
    }

    createEntity = async (
        entity: Partial<CreateCustomer | CreateEditSite | CreateAsset | CreateAssetSection>,
        modalType: ModalTypes,
    ): Promise<CustomerStoreResults> => {
        return await withLoading(
            val => (this.customerLoading = val),
            async () => {
                try {
                    // Add new function here
                    const modalFunctions = {
                        [ModalTypes.CustomerModal]: postNewCustomer,
                        [ModalTypes.SiteModal]: postNewSite,
                        [ModalTypes.AssetModal]: postNewAsset,
                        [ModalTypes.AssetSectionModal]: postNewAssetSection,
                    }

                    const modalFunction = modalFunctions[modalType]
                    const response = await modalFunction(entity as Partial<any>)

                    if (!isSuccess(response.statusCode)) {
                        return response.statusCode === 409
                            ? { passed: false, message: "Conflict-409: an entity with the same name exists." }
                            : returnOnFail()
                    }
                    const createdEntity = response.result
                    const entityId = createdEntity.id as string
                    runInAction(() => {
                        // Add new update when succeed here.
                        switch (modalType) {
                            case ModalTypes.CustomerModal:
                                this.customerMap[entityId] = observable.object(createdEntity as Customer)
                                break
                            case ModalTypes.SiteModal: {
                                const site = entity as CreateEditSite
                                console.log(site, createdEntity)
                                const customer = this.customerMap[site.customerId]
                                if (customer) {
                                    customer.sites = customer.sites || []
                                    customer.sites.push(createdEntity as Site)
                                    // Add the new site to the siteMap
                                    this.siteMap[entityId] = observable.object(createdEntity as Site)
                                }
                                break
                            }
                            case ModalTypes.AssetModal: {
                                const asset = entity as CreateAsset
                                const site = this.siteMap[asset.siteId]
                                if (site) {
                                    site.assets = site.assets || []
                                    site.assets.push(createdEntity as Asset)
                                    // Add the new asset to the assetMap
                                    this.assetMap[entityId] = observable.object(createdEntity as Asset)

                                    // Update the customer's sites list as well
                                    const customer = this.customerMap[asset.customerId]
                                    if (customer) {
                                        const updatedSites = customer.sites?.map(s => {
                                            if (s.id === site.id) {
                                                const updatedSite = { ...s }
                                                updatedSite.assets = site.assets
                                                return updatedSite
                                            }
                                            return s
                                        })
                                        customer.sites = updatedSites
                                    }
                                }
                                break
                            }
                            case ModalTypes.AssetSectionModal: {
                                const assetSection = entity as CreateAssetSection
                                const asset = this.assetMap[assetSection.assetId]
                                const site = this.siteMap[assetSection.siteId]
                                if (asset) {
                                    asset.assetSections = asset.assetSections || []
                                    asset.assetSections.push(createdEntity as AssetSection)
                                    // Add the new asset section to the assetMap
                                    this.assetSectionMap[entityId] = observable.object(createdEntity as AssetSection)

                                    // Update the customer's sites asset list as well
                                    if (site) {
                                        const updatedAssets = site.assets?.map(a => {
                                            if (a.id === asset.id) {
                                                const updatedAsset = { ...a }
                                                updatedAsset.assetSections = asset.assetSections
                                                return updatedAsset
                                            }
                                            return a
                                        })
                                        site.assets = updatedAssets
                                    }
                                    // Update the customer's sites list as well
                                    const customer = this.customerMap[assetSection.customerId]
                                    if (customer && customer.sites) {
                                        const updatedSites = customer.sites?.map(s => {
                                            if (s.id === site.id) {
                                                const updatedSite = { ...s }
                                                updatedSite.assets = site.assets
                                                return updatedSite
                                            }
                                            return s
                                        })
                                        customer.sites = updatedSites
                                    }
                                }
                                break
                            }
                            default:
                                throw new Error("Invalid modal type")
                        }
                    })
                    return { passed: true, message: "", entityId }
                } catch (error) {
                    console.error("Error creating entity:", error)
                    return { passed: false, message: "An error has occurred, please check the logs", entityId: "" }
                }
            },
        )
    }

    editEntity = async (
        entity: Partial<EditCustomer | CreateEditSite | EditAsset | EditingAssetSectionWindow>,
        modalType: ModalTypes,
    ): Promise<CustomerStoreResults> => {
        return await withLoading(
            val => (this.customerLoading = val),
            async () => {
                try {
                    // Add new function here
                    const modalFunctions = {
                        [ModalTypes.CustomerModal]: editCustomer,
                        [ModalTypes.SiteModal]: editSite,
                        [ModalTypes.AssetModal]: editAsset,
                        [ModalTypes.AssetSectionModal]: editAssetSection,
                    }

                    const modalFunction = modalFunctions[modalType]

                    const response = await modalFunction(entity as Partial<any>)

                    if (!isSuccess(response.statusCode)) {
                        return response.statusCode === 409
                            ? { passed: false, message: "Conflict-409: an entity with the same name exists." }
                            : returnOnFail()
                    }
                    const entityId = entity?.id as string

                    runInAction(() => {
                        // Add new update when succeed here.
                        switch (modalType) {
                            case ModalTypes.CustomerModal: {
                                const customer = this.customerMap[entityId]
                                if (customer) {
                                    this.customerMap[entityId] = {
                                        ...customer,
                                        ...(entity as Customer),
                                    }
                                }
                                break
                            }
                            case ModalTypes.SiteModal: {
                                const site = this.siteMap[entityId]
                                if (site) {
                                    this.siteMap[entityId] = {
                                        ...site,
                                        ...(entity as CreateEditSite),
                                    }

                                    // Update the customer's sites list as well
                                    const customerId = (entity as CreateEditSite).customerId
                                    const customer = this.customerMap[customerId]
                                    if (customer) {
                                        const updatedSites = customer.sites?.map(s =>
                                            s.id === entityId ? { ...s, ...(entity as CreateEditSite) } : s,
                                        )
                                        customer.sites = updatedSites
                                    }
                                }
                                break
                            }
                            case ModalTypes.AssetModal: {
                                const oldAsset = entity as EditAsset
                                const asset = this.assetMap[entityId]
                                if (asset) {
                                    this.assetMap[entityId] = {
                                        ...asset,
                                        ...(entity as EditAsset),
                                    }

                                    // Update the site's assets list as well
                                    const siteId = (entity as EditAsset).siteId
                                    const site = this.siteMap[siteId]
                                    let updatedAssets: Asset[]
                                    if (site) {
                                        updatedAssets = site.assets?.map(a => (a.id === entityId ? { ...a, ...(entity as EditAsset) } : a))
                                        site.assets = updatedAssets
                                    }

                                    // Update the customer's sites list if the asset is associated with a site
                                    const customerId = oldAsset?.customerId
                                    if (customerId) {
                                        const customer = this.customerMap[customerId]
                                        if (customer) {
                                            const updatedSites = customer.sites?.map(s => {
                                                if (s.id === siteId) {
                                                    const updatedSite = { ...s }
                                                    updatedSite.assets = updatedAssets
                                                    return updatedSite
                                                }
                                                return s
                                            })
                                            customer.sites = updatedSites
                                        }
                                    }
                                }
                                break
                            }

                            case ModalTypes.AssetSectionModal: {
                                const assetSection = this.assetSectionMap[entityId]
                                const editAssetSectionData = entity as EditingAssetSectionWindow

                                if (assetSection) {
                                    this.assetSectionMap[entityId] = {
                                        ...assetSection,
                                        ...editAssetSectionData,
                                    }

                                    // Update the asset's asset sections list
                                    const assetId = editAssetSectionData.assetId
                                    const asset = this.assetMap[assetId]

                                    if (asset) {
                                        const updatedAssetSections = asset.assetSections.map(as =>
                                            as.id === entityId ? { ...as, ...editAssetSectionData } : as,
                                        )

                                        asset.assetSections = updatedAssetSections

                                        // Update the site's assets list
                                        const siteId = editAssetSectionData.siteId
                                        const site = this.siteMap[siteId]

                                        if (site) {
                                            const updatedAssets = site.assets.map(a =>
                                                a.id === assetId ? { ...a, assetSections: updatedAssetSections } : a,
                                            )

                                            site.assets = updatedAssets

                                            // Update the customer's sites list
                                            const customerId = editAssetSectionData.customerId
                                            const customer = this.customerMap[customerId]

                                            if (customer && customer.sites) {
                                                const updatedSites = customer.sites.map(s => (s.id === siteId ? { ...s, assets: updatedAssets } : s))
                                                customer.sites = updatedSites
                                            }
                                        }
                                    }
                                }
                                break
                            }

                            default:
                                throw new Error("Invalid modal type")
                        }
                    })
                    return { passed: true, message: "", entityId }
                } catch (error) {
                    console.error("Error editing entity:", error)
                    return { passed: false, message: "An error has occurred, please check the logs", entityId: "" }
                }
            },
        )
    }

    deleteEntity = async (
        entityId: string,
        modalType: ModalTypes,
        customerId: string,
        siteId: string,
        assetId: string,
    ): Promise<CustomerStoreResults> => {
        return await withLoading(
            val => (this.customerLoading = val),
            async () => {
                try {
                    // Add new function here
                    const modalFunctions = {
                        [ModalTypes.CustomerModal]: deleteCustomer,
                        [ModalTypes.SiteModal]: deleteSite,
                        [ModalTypes.AssetModal]: deleteAsset,
                        [ModalTypes.AssetSectionModal]: deleteAssetSection,
                    }

                    const modalFunction = modalFunctions[modalType]

                    const response = await modalFunction(entityId as string)

                    if (!isSuccess(response.statusCode)) {
                        return response.statusCode === 409
                            ? { passed: false, message: "Conflict-409: an entity with the same name exists." }
                            : returnOnFail()
                    }

                    runInAction(() => {
                        // Add new update when succeed here.
                        switch (modalType) {
                            case ModalTypes.CustomerModal: {
                                delete this.customerMap[entityId]
                                break
                            }
                            case ModalTypes.SiteModal: {
                                delete this.siteMap[entityId]
                                // Update the customer's sites list by removing the deleted site
                                const customer = this.customerMap[customerId]
                                if (customer && customer.sites) {
                                    customer.sites = customer.sites.filter(s => s.id !== entityId)
                                }
                                break
                            }
                            case ModalTypes.AssetModal: {
                                // Update the assetSectionMap if the deleted asset had any assetSections
                                const asset = this.assetMap[entityId]
                                if (asset && asset.assetSections) {
                                    asset.assetSections.forEach(assetSection => {
                                        delete this.assetSectionMap[assetSection.id]
                                    })
                                }
                                delete this.assetMap[entityId]
                                // Update the site's assets list by removing the deleted asset
                                const site = this.siteMap[siteId]
                                if (site && site.assets) {
                                    site.assets = site.assets.filter(a => a.id !== entityId)
                                }

                                // Update the customer's sites list if the asset is associated with a site
                                if (customerId) {
                                    const customer = this.customerMap[customerId]
                                    if (customer && customer.sites) {
                                        const updatedSites = customer.sites.map(s => {
                                            if (s.id === siteId) {
                                                const updatedSite = { ...s }
                                                updatedSite.assets = site.assets
                                                return updatedSite
                                            }
                                            return s
                                        })
                                        customer.sites = updatedSites
                                    }
                                }
                                break
                            }
                            case ModalTypes.AssetSectionModal: {
                                delete this.assetSectionMap[entityId]

                                // Update the asset's assetSections list by removing the deleted asset section
                                const asset = this.assetMap[assetId]
                                if (asset && asset.assetSections) {
                                    asset.assetSections = asset.assetSections.filter(as => as.id !== entityId)
                                }

                                // Update the site's assets list by removing the deleted asset section
                                const site = this.siteMap[siteId]
                                if (site && site.assets) {
                                    site.assets = site.assets.map(a => {
                                        if (a.id === assetId) {
                                            const updatedAsset = { ...a }
                                            updatedAsset.assetSections = updatedAsset.assetSections.filter(as => as.id !== entityId)
                                            return updatedAsset
                                        }
                                        return a
                                    })
                                }

                                // Update the customer's sites list if the asset is associated with a site
                                const customer = this.customerMap[customerId]
                                if (customer && customer.sites) {
                                    const updatedSites = customer.sites.map(s => {
                                        const updatedSite = { ...s }
                                        updatedSite.assets = updatedSite.assets.map(a => {
                                            if (a.id === assetId) {
                                                const updatedAsset = { ...a }
                                                updatedAsset.assetSections = updatedAsset.assetSections.filter(as => as.id !== entityId)
                                                return updatedAsset
                                            }
                                            return a
                                        })
                                        return updatedSite
                                    })
                                    customer.sites = updatedSites
                                }

                                break
                            }
                        }
                        return { passed: true, message: "" }
                    })
                    return { passed: true, message: "" }
                } catch (error) {
                    console.error("Error creating customer:", error)
                    return { passed: false, message: "An error has occurred, please check the logs" }
                }
            },
        )
    }
}
export default new CustomerStore()
